import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import AirportDropdownOption from 'components/Flights/FlightsSearch/SearchWidget/FlightAirportSelect/AirportDropdownOption'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import usePopularAirports from 'hooks/Flights/usePopularAirports'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ListSubheader from 'LuxKit/List/ListSubheader'
import BodyText from 'LuxKit/Typography/BodyText'
import LineExclamationIcon from 'LuxKit/Icons/line/LineExclamationIcon'
import zIndex from 'styles/tools/z-index'
import { rem } from 'polished'
import { forwardRef, useCallback, useContext, useEffect, useId, useImperativeHandle, useRef, useState } from 'react'
import type { ChangeEvent, FocusEvent, InvalidEvent, KeyboardEvent } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import {
  airportToSearchItem,
  getAirportTransferLocationName,
  getAirportTransferLocationSearchTypes,
} from 'lib/airportTransfer/airportTransferUtils'
import CarsAirportTransferLocationSearchResults from './CarsAirportTransferLocationSearchResults'

const FieldWrapper = styled.div`
  position: relative;
  display: grid;
  align-items: center;
`

const ValidationBubble = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  bottom: calc(100% + ${rem(8)});
  inset-inline-start: 0;
  width: max-content;
  max-width: min(${rem(335)}, calc(100vw - ${rem(32)}));
  padding: ${rem(8)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow: ${props => props.theme.shadow.bottom.medium};
  z-index: ${zIndex.tooltip};
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    bottom: ${rem(-5)};
    inset-inline-start: ${rem(16)};
    width: ${rem(8)};
    height: ${rem(8)};
    background-color: inherit;
    border-inline-end: inherit;
    border-bottom: inherit;
    transform: rotate(45deg);

    [dir='rtl'] & {
      transform: rotate(-45deg);
    }
  }
`

const ValidationIcon = styled(LineExclamationIcon)`
  flex-shrink: 0;
  border-radius: ${rem(2)};
  background-color: ${props => props.theme.palette.messaging.warning.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const FormField = styled(TextInputOnly)`
  border-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  padding: 0 ${rem(8)};

  :focus-within {
    border-color: ${props => props.theme.palette.neutral.surface.normalContrast};
  }
`

export interface CarsAirportTransferLocationInputHandle {
  focus: () => void
}

interface Props {
  className?: string
  isSecondaryLocation?: boolean
  placeholder: string
  onLocationSelect: (nextSearchItem: App.SearchItem) => void
  onLocationsOpen: () => void
  onLocationsClose: () => void
}

const CarsAirportTransferLocationInput = forwardRef<CarsAirportTransferLocationInputHandle, Props>(function CarsAirportTransferLocationInput(props, ref) {
  const { className, isSecondaryLocation = false, placeholder, onLocationSelect, onLocationsOpen, onLocationsClose } = props

  const intl = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const isEditingRef = useRef(false)
  const warningId = useId()
  const listboxId = useId()
  const [results, setResults] = useState<Array<App.SearchItem>>([])
  const [activeIndex, setActiveIndex] = useState(-1)
  const [warning, setWarning] = useState<string>()
  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const { searchItem, secondarySearchItem } = useContext(GlobalSearchStateContext)
  let selectedItem = searchItem
  let alternateSearchItem = secondarySearchItem

  if (isSecondaryLocation) {
    selectedItem = secondarySearchItem
    alternateSearchItem = searchItem
  }

  const searchTypes = getAirportTransferLocationSearchTypes(alternateSearchItem)

  const alternateLocationIsAirport = alternateSearchItem?.searchType === 'airport'
  const locationPlaceholder = alternateLocationIsAirport
    ? intl.formatMessage({
        id: 'gSzpbiRShZ1HS_PkGKdO',
        defaultMessage: 'Search hotel name or address',
        description: 'Placeholder for the non-airport location in airport transfer search',
      })
    : placeholder
  const popularAirports = usePopularAirports('departure')
  const [isOpen, setIsOpen] = useState(false)
  const [searchPhrase, setSearchPhrase] = useState('')

  const inputValue = getAirportTransferLocationName(selectedItem) ?? searchPhrase

  useIsomorphicLayoutEffect(() => {
    inputRef.current?.setCustomValidity(!selectedItem && inputValue
      ? intl.formatMessage({
          id: 'LL7i26ysnH4r6SflqSd2',
          defaultMessage: 'Please select a location from the suggestions',
          description: 'Validation message when a transfer location has been typed but no suggestion has been selected',
        })
      : '')
  }, [inputValue, intl, selectedItem])

  const openDropdown = useCallback(() => {
    setIsOpen(true)
    onLocationsOpen()
  }, [onLocationsOpen])

  const closeDropdown = useCallback(() => {
    setActiveIndex(-1)
    setIsOpen(false)
    onLocationsClose()
  }, [onLocationsClose])

  const onInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    isEditingRef.current = true
    setWarning(undefined)
    const nextSearchPhrase = event.currentTarget.value
    setSearchPhrase(nextSearchPhrase)
    setActiveIndex(-1)

    if (isSecondaryLocation) {
      searchDispatch({ type: GlobalSearchStateActions.UNSET_SECONDARY_SEARCH_ITEM })
    } else {
      searchDispatch({ type: GlobalSearchStateActions.UNSET_SEARCH_ITEM })
    }

    if (!nextSearchPhrase) {
      closeDropdown()
      return
    }

    if (!isOpen) {
      openDropdown()
    }
  }, [closeDropdown, isSecondaryLocation, searchDispatch, isOpen, openDropdown])

  const onValidationAttempt = useCallback(() => {
    isEditingRef.current = false
  }, [])

  const onInvalid = useCallback((event: InvalidEvent<HTMLInputElement>) => {
    // Replace the browser bubble so the warning cannot cover the suggestions.
    event.preventDefault()
    const input = event.currentTarget
    const firstInvalidInput = input.form && Array.from(input.form.elements).find(element =>
      element instanceof HTMLInputElement && !element.validity.valid,
    )
    if (firstInvalidInput === input) {
      // Focus after native validation has finished checking the remaining fields.
      requestAnimationFrame(() => {
        if (!input.validity.valid) input.focus()
      })
      setWarning(input.validationMessage)
    }
  }, [])

  const onInputValidate = useCallback((input: HTMLInputElement) => {
    // Keep native submission blocked without showing an error during editing.
    return isEditingRef.current ? undefined : input.validity
  }, [])

  const onLocationResultSelect = useCallback((nextSearchItem?: App.SearchItem) => {
    if (nextSearchItem) {
      onLocationSelect(nextSearchItem)
    }

    closeDropdown()
  }, [closeDropdown, onLocationSelect])

  const onPopularAirportSelect = useCallback((airport: App.AirportLocation) => {
    onLocationResultSelect(airportToSearchItem(airport))
  }, [onLocationResultSelect])

  const onResultsChange = useCallback((results: Array<App.SearchItem>) => {
    setResults(results)
    setActiveIndex(-1)
    searchDispatch({ type: GlobalSearchStateActions.SET_SUGGESTED_SEARCH_ITEMS, searchItems: results })
  }, [searchDispatch])

  const onFocus = useCallback(() => {
    if (!inputValue && alternateLocationIsAirport) {
      return
    }

    openDropdown()
  }, [alternateLocationIsAirport, inputValue, openDropdown])

  const onBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
    if (event.relatedTarget === inputRef.current) return
    if (event.relatedTarget instanceof Node && dropdownRef.current?.contains(event.relatedTarget)) return
    closeDropdown()
  }, [closeDropdown])

  const activeOptionId = isOpen && results[activeIndex] ? `${listboxId}-${activeIndex}` : undefined

  useEffect(() => {
    if (activeOptionId) {
      document.getElementById(activeOptionId)?.scrollIntoView({ block: 'nearest' })
    }
  }, [activeOptionId])

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing || event.keyCode === 229) return

    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && searchPhrase) {
      event.preventDefault()
      if (!isOpen) openDropdown()
      if (results.length) {
        setActiveIndex(index => {
          if (!isOpen || index < 0) return event.key === 'ArrowDown' ? 0 : results.length - 1
          return Math.max(0, Math.min(results.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)))
        })
      }
    } else if (event.key === 'Enter' && isOpen) {
      // Choosing a suggestion must never submit the surrounding search form.
      event.preventDefault()
      event.stopPropagation()
      if (results[activeIndex]) onLocationResultSelect(results[activeIndex])
    } else if (event.key === 'Escape' && isOpen) {
      event.preventDefault()
      event.stopPropagation()
      closeDropdown()
    } else if (event.key === 'Tab') {
      closeDropdown()
    }
  }, [activeIndex, closeDropdown, isOpen, onLocationResultSelect, openDropdown, results, searchPhrase])

  const focus = useCallback(() => {
    inputRef.current?.focus()
  }, [])

  useImperativeHandle(ref, () => ({ focus }), [focus])

  return (
    <FieldWrapper onBlur={onBlur}>
      <FormField
        noValidationMessage
        noValidationSpacing
        placeholder={locationPlaceholder}
        ref={inputRef}
        value={inputValue}
        onChange={onInputChange}
        onFocus={onFocus}
        onClick={onFocus}
        onKeyDown={onKeyDown}
        role={searchPhrase ? 'combobox' : undefined}
        aria-expanded={searchPhrase ? isOpen : undefined}
        aria-autocomplete={searchPhrase ? 'list' : undefined}
        aria-controls={searchPhrase && isOpen ? listboxId : undefined}
        aria-activedescendant={activeOptionId}
        onBlurCapture={onValidationAttempt}
        onInvalidCapture={onValidationAttempt}
        onInvalid={onInvalid}
        onInputValidate={onInputValidate}
        aria-describedby={warning && !selectedItem ? warningId : undefined}
        className={className}
        required
      />
      {warning && !selectedItem && <ValidationBubble
        id={warningId}
        role="alert"
      >
        <ValidationIcon size="XS" aria-hidden />
        <BodyText variant="small">{warning}</BodyText>
      </ValidationBubble>}
      <DropdownList
        ref={dropdownRef}
        anchorRef={inputRef}
        open={isOpen}
        size="M"
        triggerRef={inputRef}
        onClose={closeDropdown}
        portaled
        placement="bottom-start"
      >
        {searchPhrase && <CarsAirportTransferLocationSearchResults
          searchPhrase={searchPhrase}
          searchTypes={searchTypes}
          alternateAirportCode={alternateSearchItem?.searchType === 'airport' ? alternateSearchItem.airportCode : undefined}
          onChange={onLocationResultSelect}
          onResultsChange={onResultsChange}
          listboxId={listboxId}
          listboxLabel={locationPlaceholder}
          activeIndex={isOpen ? activeIndex : -1}
        />}
        {!searchPhrase && !alternateLocationIsAirport && <>
          <ListSubheader>
            {intl.formatMessage({
              id: 'HpwgUx3qukzE2QIVxieZ',
              defaultMessage: 'Popular airports',
              description: 'Heading for popular airports in airport transfer location search',
            })}
          </ListSubheader>
          {popularAirports.map(airport => <AirportDropdownOption
            key={airport.airportCode}
            airport={airport}
            onSelectAirport={onPopularAirportSelect}
          />)}
        </>}
      </DropdownList>
    </FieldWrapper>
  )
})

export default CarsAirportTransferLocationInput
